import { useState, useMemo, useEffect } from 'react'
import CreateSystemAlertPopup from './Modal/CreateSystemAlertPopup'
import { KTSVG } from '../../../../../_metronic/helpers'
import {
  useLazyDeleteSystemAlertQuery,
  useLazyGetAllSystemAlertQuery,
} from '../../../../../services/cellcast'
import { OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap'
import moment from 'moment-timezone'
import ReactPaginate from 'react-paginate'
import { useLoadedData } from '../../../../hooks/useGetLoadedData'
import DeleteConfirmationPopup from '../../../../Components/DeleteConfirmationPopup'
import ReactDatePicker from 'react-datepicker'
import Select from 'react-select'
import { toast } from 'react-toastify'
import { useTimezone } from '../../../../hooks/useTimezone'

const MethodOptions = [
  {
    value: 'banner',
    label: 'Banner',
  },
  {
    value: 'email',
    label: 'Email',
  },
  {
    value: 'inbox',
    label: 'Inbox',
  },
]

const SystemAlert = () => {
  // State to control modal open/close
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [triggerFetch, setTriggerFetch] = useState(false)
  const { calculate, loadedData, setLoadedData } = useLoadedData()

  const today = new Date()
  const sevenDaysAgo = new Date(today.getTime() - 120 * 24 * 60 * 60 * 1000)
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([sevenDaysAgo, new Date()])
  const [startDate, endDate] = dateRange

  const { userTimezone } = useTimezone()

  const setDateParams = (dateRangeParams: [Date | null, Date | null]) => {
    let [startDate, endDate] = dateRangeParams

    if (endDate === null) {
      endDate = new Date()
    }

    setParams({
      ...params,
      startAt: moment(startDate).format('YYYY-MM-DD'),
      endAt: moment(endDate).format('YYYY-MM-DD'),
    })
    setDateRange(dateRangeParams)
    setTriggerFetch(true)
  }

  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    methods: '',
    startAt: moment(startDate).format('YYYY-MM-DD'),
    endAt: moment(endDate).format('YYYY-MM-DD'),
  })
  const [isOpenConfirmation, setIsOpenConfirmation] = useState(false)
  const [cancelParam, setCancelParam] = useState<{
    id: string
  } | null>(null)

  const CreateSystemAlert = useMemo(
    () => (
      <CreateSystemAlertPopup show={showCreateModal} onClose={() => {
        setTriggerFetch(true)
        setShowCreateModal(false)
      }} />
    ),
    [showCreateModal]
  )

  const [fetchSystemAlert, { isLoading, data, isFetching }] = useLazyGetAllSystemAlertQuery()
  const [deleteSystemAlert, { isLoading: loadingDelete }] = useLazyDeleteSystemAlertQuery()

  useEffect(() => {
    fetchSystemAlert(params)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (triggerFetch && triggerFetch === true) {
      fetchSystemAlert(params);
    }
    setTriggerFetch(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerFetch])

  useEffect(() => {
    if (data) {
      const loadedData = calculate(
        data && data.data.current ? data.data.current : 1,
        params.limit,
        data && data.data.total ? data.data.total : 0
      )
      setLoadedData(loadedData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isFetching])

  const cancelConfirmation = useMemo(
    () => (
      <DeleteConfirmationPopup
        show={isOpenConfirmation}
        handleClose={() => {
          setIsOpenConfirmation(false)
          setCancelParam(null)
        }}
        Message={'Are you sure, you want to delete the alert ?'}
        confirm={async () => {
          if (cancelParam) {
            const result = await deleteSystemAlert({ id: cancelParam.id })
            if ('data' in result) {
              toast.success(result.data?.message || 'System alert deleted successfully')
              setTriggerFetch(true)
            } else if ('error' in result) {
              const errObj = result.error as any
              if (errObj.data?.message) {
                toast.error(errObj.data.message)
              } else {
                toast.error('Failed to delete system alert')
              }
            }

            setCancelParam(null)
          }
        }}
        confirmBtnTitle='Yes, Delete Alert'
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpenConfirmation, cancelParam]
  )

  return (
    <>
      {CreateSystemAlert}
      {cancelConfirmation}
      <div className={`card`}>
        <div className='card-header border-0 p-0 align-items-center mb-10'>
          <div className='d-flex flex-column w-100'>
            <h3 className='card-title align-items-start  flex-column'>
              <span className='card-label fw-bolder fs-2 mb-1'>System Alert</span>
            </h3>
            <div className='d-flex justify-content-end'>
              <div className='d-flex align-items-start col-md-3 me-2'>
                <KTSVG
                  path='/media/icons/duotune/general/gen021.svg'
                  className='svg-icon-1 position-absolute ms-6 mt-3'
                />
                <Select
                      value={MethodOptions.find(opt => opt.value === params.methods)}
                      className='form-select form-select-solid fw-bolder'
                      name='industry'
                      onChange={(e: any) => {
                        setParams({...params, methods: e?.value})
                        setTriggerFetch(true)
                      }}
                      options={MethodOptions}
                    />
              </div>
              <div className='col-md-3'>
                <div className='d-flex'>
                  <ReactDatePicker
                    selectsRange={true}
                    dateFormat='MMM d, yyyy'
                    className=' border-0 form-control form-control-lg form-control-solid'
                    placeholderText='Pick data & time'
                    onChange={(update) => {
                      setDateParams(update)
                    }}
                    startDate={startDate}
                    endDate={endDate}
                    disabled={isLoading}
                  />
                  <span
                    className='input-group-text'
                    style={{ backgroundColor: '#f5f8fa', borderRadius: 0, borderColor: '#f5f8fa' }}
                  >
                    <i className='bi bi-calendar'></i>
                  </span>
                </div>
              </div>

              <div className='ms-2'>
                <button className='btn btn-primary ml-auto' onClick={() => setShowCreateModal(true)}>
                  <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3' />
                  Add Alert
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className='card-body py-3'>
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-400 text-muted '>
                  <th className='ps-4 text-start'>Title</th>
                  <th className='ps-4 text-start min-w-200px'>Message</th>
                  <th className='ps-4 text-start'>Start</th>
                  <th className='ps-4 text-start min-w-100px'>End</th>
                  <th className='ps-4 text-start'>Recipients</th>
                  <th className='ps-4 text-start'>Channel</th>
                  <th className='ps-4 text-center'>Action</th>
                </tr>
              </thead>
              <tbody className='text-gray-600'>
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className='text-center'>
                      <Spinner />
                    </td>
                  </tr>
                ) : (
                  <>
                    {data?.data?.items?.map((item, idx) => (
                      <tr key={idx} className='text-muted '>
                        <td>
                          {item?.title}
                        </td>
                        <td>
                          <span dangerouslySetInnerHTML={{ __html: item.message }} />
                        </td>
                        <td>
                          {moment.tz(item?.startAt, userTimezone).format('DD/MM/YYYY hh:mm')}
                        </td>
                        <td>
                          {moment.tz(item?.endAt, userTimezone).format('DD/MM/YYYY hh:mm')}
                        </td>
                        <td>
                          <td>
                            {Array.isArray(item?.recipients)
                              ? item.recipients?.length
                              : item?.recipients || '-'}
                          </td>
                        </td>
                        <td>
                          <td>{item?.methods?.join(', ')}</td>
                        </td>
                        <td>
                          <OverlayTrigger
                            placement='top'
                            overlay={<Tooltip id='button-tooltip'>Delete</Tooltip>}
                          >
                            <button
                              className='btn btn-disable btn-sm btn-icon btn-bg-light btn-danger ms-3'
                              onClick={() => {
                                setCancelParam({ id: item?._id })
                                setIsOpenConfirmation(true)
                              }}
                            >
                              {loadingDelete ? (
                                <Spinner />
                              ) : (
                                <i className='bi bi-x fs-5'></i>
                              )}
                            </button>
                          </OverlayTrigger>
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
          <div className='abcd'>
            <select
              onChange={(e: any) => {
                setParams({ ...params, limit: e.target.value })
                setTriggerFetch(true)
              }}
              value={params.limit || 10}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            {isLoading
              ? '. . . . .'
              : `Show ${loadedData.startLoad} - ${loadedData.endLoad} of ${data?.data?.total ? data?.data?.total : 0
              } Alert`}
            <ReactPaginate
              previousLabel='<'
              nextLabel='>'
              pageCount={data?.data?.totalPages as number}
              onPageChange={(e: any) => {
                setParams({ ...params, page: e.selected + 1 })
                setTriggerFetch(true)
              }}
              containerClassName={'pagination'}
              previousLinkClassName={'pagination__link'}
              nextLinkClassName={'pagination__link'}
              disabledClassName={'pagination__link--disabled'}
              activeClassName={'pagination__link--active'}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default SystemAlert
